import { useInView } from '../hooks/useInView';
import './ContactSection.css';

const CONTACT_CARDS = [
  {
    accent: 'yellow',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M19 15.5A1.5 1.5 0 0 1 17.5 17h-13A1.5 1.5 0 0 1 3 15.5v-9A1.5 1.5 0 0 1 4.5 5h13A1.5 1.5 0 0 1 19 6.5v9z"
              stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 6.5l8 5.5 8-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Future Availability',
    desc: 'Interested in TaxiOps for your transportation company? Contact Old Alex Hub to express your interest for consideration in future phases.',
    cta: 'Express Future Interest',
    mailto: 'mailto:info@oldalexhub.com?subject=TaxiOps Future Availability Inquiry',
    note: 'Availability is handled directly by Old Alex Hub.',
  },
  {
    accent: 'blue',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 3L4 8v11h5v-5h4v5h5V8L11 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Strategic Partnership',
    desc: 'For partnership opportunities, integration discussions, or enterprise transportation deployment inquiries.',
    cta: 'Discuss Partnership',
    mailto: 'mailto:info@oldalexhub.com?subject=TaxiOps Strategic Partnership Inquiry',
    note: 'Open to partnership conversations.',
  },
  {
    accent: 'green',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M11 7v3.5l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9 15h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'General Information',
    desc: 'For general questions about TaxiOps, its capabilities, its launch progress, or Old Alex Hub.',
    cta: 'Send Information Request',
    mailto: 'mailto:info@oldalexhub.com?subject=TaxiOps General Information Request',
    note: 'We will respond via email.',
  },
];

const ACCENT_MAP = {
  yellow: { border: 'var(--border-yellow)', iconBg: 'var(--yellow-glow)', iconBorder: 'var(--border-yellow)', iconColor: 'var(--yellow)', btnClass: 'btn--outline-yellow' },
  blue:   { border: 'var(--border-blue)',   iconBg: 'var(--blue-glow)',   iconBorder: 'var(--border-blue)',   iconColor: 'var(--blue-light)', btnClass: 'btn--outline' },
  green:  { border: 'rgba(16,185,129,0.25)', iconBg: 'var(--green-glow)', iconBorder: 'rgba(16,185,129,0.25)', iconColor: 'var(--green-light)', btnClass: 'btn--outline' },
};

export default function ContactSection() {
  const [ref, inView] = useInView();

  return (
    <section className="contact-section" id="contact" aria-labelledby="contact-heading">
      <div className="contact-section__divider" aria-hidden="true" />
      <div className="container" ref={ref}>
        {/* Header */}
        <div className="contact-section__header">
          <p className={`section-label fade-up ${inView ? 'in-view' : ''}`}>
            Contact
          </p>
          <h2
            id="contact-heading"
            className={`contact-section__heading fade-up d1 ${inView ? 'in-view' : ''}`}
          >
            Connect with<br />
            <span className="gradient-text">Old Alex Hub</span>
          </h2>
          <p className={`contact-section__sub fade-up d2 ${inView ? 'in-view' : ''}`}>
            TaxiOps availability is handled directly by Old Alex Hub. For future
            availability inquiries, partnership discussions, or general information,
            contact Old Alex Hub at{' '}
            <a href="mailto:info@oldalexhub.com" className="contact-section__email">
              info@oldalexhub.com
            </a>
          </p>
        </div>

        {/* Contact cards */}
        <div className="contact-section__cards">
          {CONTACT_CARDS.map((card, i) => {
            const a = ACCENT_MAP[card.accent];
            return (
              <div
                key={card.title}
                className={`contact-card fade-up d${i + 2} ${inView ? 'in-view' : ''}`}
                style={{ '--card-border': a.border }}
              >
                <div
                  className="contact-card__icon"
                  style={{ background: a.iconBg, border: `1px solid ${a.iconBorder}`, color: a.iconColor }}
                  aria-hidden="true"
                >
                  {card.icon}
                </div>
                <h3 className="contact-card__title">{card.title}</h3>
                <p className="contact-card__desc">{card.desc}</p>
                <div className="contact-card__footer">
                  <a href={card.mailto} className={`btn ${a.btnClass} contact-card__btn`}>
                    {card.cta}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2 7h10M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                  <span className="contact-card__note">{card.note}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Direct email CTA */}
        <div className={`contact-section__direct fade-up d5 ${inView ? 'in-view' : ''}`}>
          <div className="contact-section__direct-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M17 13.5A1.5 1.5 0 0 1 15.5 15h-11A1.5 1.5 0 0 1 3 13.5v-7A1.5 1.5 0 0 1 4.5 5h11A1.5 1.5 0 0 1 17 6.5v7z"
                    stroke="currentColor" strokeWidth="1.4"/>
              <path d="M3 6l7 5 7-5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <p className="contact-section__direct-label">Direct Contact</p>
            <a
              href="mailto:info@oldalexhub.com"
              className="contact-section__direct-email"
            >
              info@oldalexhub.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
